import React, { useEffect, useState, Suspense, lazy } from "react";
import { View, AppState, StyleSheet, Linking } from "react-native";

import { NavigationContainer } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import type { NativeStackNavigationOptions } from "@react-navigation/native-stack";
import { StatusBar } from "expo-status-bar";
import { ActivityIndicator, Snackbar } from "react-native-paper";

// Lazy load heavy modal components for better web performance
const ContactModal = lazy(() => import("./components/ContactModal"));
const DailiesModal = lazy(() => import("./components/DailiesModal"));
const GameReportModal = lazy(() => import("./components/GameReportModal"));
const QuickstartModal = lazy(() => import("./components/QuickstartModal"));
const StatsModal = lazy(() => import("./components/StatsModal"));
// Legal page components

import { AppProviders } from "./components/AppProviders";
import ErrorBoundary from "./components/ErrorBoundary";
import { Footer } from "./components/Footer";
import { LegalPage } from "./components/LegalPages";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { TutorialProvider } from "./context/TutorialContext";
import GameScreen from "./screens/GameScreen";
import { gameFlowManager } from "./services/GameFlowManager";
import { unifiedDataStore } from "./services/UnifiedDataStore";
import { appInitializer } from "./services/AppInitializer";
import { useGameStore } from "./stores/useGameStoreFacade";
import { useModalStore } from "./stores/useModalStore";
import { useAppReadyStore } from "./stores/useAppReadyStore";

import "./utils/productionLogger"; // Initialize production logging optimizations

const LOADING_MAX_MS = 8000;

console.log("src/App.tsx: Top-level import complete");

// Define the root stack parameter list
export type RootStackParamList = {
  Home: undefined;
  Synapse: undefined;
  History: undefined;
};

// Custom screen transition animations
const customScreenOptions: Partial<NativeStackNavigationOptions> = {
  animation: "fade",
  animationDuration: 300, // in ms
};

const Stack = createNativeStackNavigator<RootStackParamList>();

// Loading fallback component for lazy-loaded modals
const modalLoadingStyles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
});

const ModalLoadingFallback: React.FC = () => (
  <View style={modalLoadingStyles.container}>
    <ActivityIndicator size="small" />
  </View>
);

function AppContent() {
  console.log("src/App.tsx: AppContent component rendering");
  const { theme } = useTheme();
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [currentLegalPage, setCurrentLegalPage] = useState<
    "terms" | "privacy" | "dmca" | "about" | "contact" | null
  >(null);
  // Removed unused initializationState and loadInitialData

  // Modal visibility states from dedicated modal store
  const quickstartModalVisible = useModalStore(
    (state) => state.quickstartModalVisible,
  );
  const contactModalVisible = useModalStore(
    (state) => state.contactModalVisible,
  );

  // Modal actions from dedicated modal store
  const setQuickstartModalVisible = useModalStore(
    (state) => state.setQuickstartModalVisible,
  );
  const setContactModalVisible = useModalStore(
    (state) => state.setContactModalVisible,
  );

  // Global message state for challenge messages
  const globalMessageVisible = useGameStore(
    (state) => state.globalMessageVisible,
  );
  const globalMessage = useGameStore((state) => state.globalMessage);
  const hideGlobalMessage = useGameStore((state) => state.hideGlobalMessage);

  // Show global messages from GameFlowManager in snackbar
  useEffect(() => {
    if (globalMessageVisible && globalMessage) {
      setSnackbarMessage(globalMessage);
      setSnackbarVisible(true);
    }
  }, [globalMessageVisible, globalMessage]);

  // Clear any problematic cached messages from older app versions
  useEffect(() => {
    // Clear localStorage items that might contain the old problematic message
    if (typeof window !== "undefined" && window.localStorage) {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        try {
          const value = localStorage.getItem(key);
          if (
            value &&
            typeof value === "string" &&
            (value.includes("session data lost") ||
              value.includes("account isn't upgraded"))
          ) {
            console.log(
              "🧹 Clearing problematic cached message from localStorage:",
              key,
            );
            localStorage.removeItem(key);
          }
        } catch {
          // Ignore errors in case the value isn't a string
        }
      });
    }

    // Also clear sessionStorage items that might contain the old problematic message
    if (typeof window !== "undefined" && window.sessionStorage) {
      const sessionKeys = Object.keys(sessionStorage);
      sessionKeys.forEach((key) => {
        try {
          const value = sessionStorage.getItem(key);
          if (
            value &&
            typeof value === "string" &&
            (value.includes("session data lost") ||
              value.includes("account isn't upgraded"))
          ) {
            console.log(
              "🧹 Clearing problematic cached message from sessionStorage:",
              key,
            );
            sessionStorage.removeItem(key);
          }
        } catch {
          // Ignore errors in case the value isn't a string
        }
      });
    }
  }, []);

  // Listen for global messages to show snackbar

  // Initialize app flow
  const initializeApp = async () => {
    try {
      console.log("🎮 App: Starting initialization...");
      const result = await appInitializer.initialize();

      // Handle the initialization result
      if (result.status === "legal-page") {
        setCurrentLegalPage(result.page);
      } else if (result.status === "ready") {
        await gameFlowManager.executeFlowDecision(result.flowDecision);
        appInitializer.scheduleBackgroundWarm();
      } else if (result.status === "error") {
        console.error("🎮 App: Initialization error:", result.error);
      }

      console.log("🎮 App: Initialization completed:", result.status);
      useAppReadyStore.getState().setAppReady(true);
    } catch (error) {
      console.error("🎮 App: Initialization failed:", error);
      useAppReadyStore.getState().setAppReady(true);
    }
  };

  // Simplified initialization using AppInitializer
  useEffect(() => {
    initializeApp();
  }, []);

  // Safety net: never trap the user behind the loading overlay.
  useEffect(() => {
    const timer = setTimeout(
      () => useAppReadyStore.getState().setAppReady(true),
      LOADING_MAX_MS,
    );
    return () => clearTimeout(timer);
  }, []);

  // Simplified deep link handling using AppInitializer
  useEffect(() => {
    const handleDeepLink = async (event: { url: string }) => {
      try {
        const result = await appInitializer.handleDeepLink(event.url);

        // Handle the deep link result
        if (result.status === "error") {
          console.error("🎮 App: Deep link handling error:", result.error);
        }
      } catch (error) {
        console.error("🎮 App: Deep link handling failed:", error);
      }
    };

    const subscription = Linking.addEventListener("url", handleDeepLink);
    return () => subscription.remove();
  }, []);

  // Set up AppState handling to flush pending data saves when app backgrounds
  useEffect(() => {
    const handleAppStateChange = async (nextAppState: string) => {
      if (nextAppState === "background" || nextAppState === "inactive") {
        // Flush any pending saves before the app goes to background
        try {
          await unifiedDataStore.flushPendingChanges();
          console.log("✅ Flushed pending data saves on app background");
        } catch (error) {
          console.error("❌ Error flushing pending saves:", error);
        }
      }
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange,
    );
    return () => subscription?.remove();
  }, []);

  console.log("src/App.tsx: AppContent component rendered");

  // Show legal page or preview image if requested
  if (currentLegalPage) {
    const legalPageStyles = StyleSheet.create({
      container: {
        flex: 1,
        backgroundColor: theme.colors.background,
      },
    });

    return (
      <AppProviders theme={theme}>
        <View style={legalPageStyles.container}>
          <LegalPage
            type={currentLegalPage}
            onBack={() => setCurrentLegalPage(null)}
          />
          <Footer onLegalPageRequest={(page) => setCurrentLegalPage(page)} />
        </View>
        <StatusBar style={theme.dark ? "light" : "dark"} />
      </AppProviders>
    );
  }

  return (
    <AppProviders theme={theme}>
      <NavigationContainer>
        <Stack.Navigator
          initialRouteName="Synapse"
          screenOptions={{
            headerShown: false,
            animation: customScreenOptions.animation,
            animationDuration: customScreenOptions.animationDuration,
            contentStyle: { backgroundColor: theme.colors.background },
          }}
        >
          <Stack.Screen name="Synapse">
            {(props) => (
              <ErrorBoundary>
                <GameScreen
                  {...props}
                  onLegalPageRequest={(page) => setCurrentLegalPage(page)}
                />
              </ErrorBoundary>
            )}
          </Stack.Screen>
        </Stack.Navigator>

        {/* Modals */}
        <Suspense fallback={<ModalLoadingFallback />}>
          <QuickstartModal
            visible={quickstartModalVisible}
            onDismiss={() => setQuickstartModalVisible(false)}
          />
        </Suspense>
        <Suspense fallback={<ModalLoadingFallback />}>
          {contactModalVisible && (
            <ContactModal
              visible={contactModalVisible}
              onDismiss={() => setContactModalVisible(false)}
            />
          )}
        </Suspense>
        <Suspense fallback={<ModalLoadingFallback />}>
          <StatsModal />
        </Suspense>
        <Suspense fallback={<ModalLoadingFallback />}>
          <DailiesModal />
        </Suspense>
        {/* Game Report Modal - needs to be at top level to appear over other modals */}
        <Suspense fallback={<ModalLoadingFallback />}>
          <GameReportModal />
        </Suspense>
      </NavigationContainer>

      {/* Global Snackbar for payment and challenge messages */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => {
          setSnackbarVisible(false);
          // Also hide global message if it's currently showing
          if (globalMessageVisible) {
            hideGlobalMessage();
          }
        }}
        duration={6000}
        style={styles.snackbar}
      >
        {snackbarMessage}
      </Snackbar>

      <StatusBar style={theme.dark ? "light" : "dark"} />
    </AppProviders>
  );
}

function App() {
  console.log("src/App.tsx: App component rendering");

  return (
    <TutorialProvider>
      <AppContent />
    </TutorialProvider>
  );
}

const AppContainer: React.FC = () => {
  console.log("src/App.tsx: AppContainer component rendering");
  return <App />;
};

export default function AppWrapper() {
  console.log("src/App.tsx: AppWrapper component rendering");
  return (
    <ThemeProvider>
      <AppContainer />
    </ThemeProvider>
  );
}

const styles = StyleSheet.create({
  snackbar: {
    marginBottom: 50,
  },
});
